/**
 * PDF padrao da proposta comercial (jsPDF).
 * Usado por Calculadora Solar e Consulta de Propostas.
 * NAO duplicar este layout nos componentes.
 */

import jsPDF from 'jspdf'
import logoHangSix from '../../HangSixLogo.png'
import autoconsumoImg from '../../img_instalacao/Autoconsumo.png'
import instalacao01 from '../../img_instalacao/Instalacao01.jpeg'
import instalacao02 from '../../img_instalacao/Instalacao02.jpeg'
import instalacao03 from '../../img_instalacao/Instalacao03.jpeg'
import instalacao04 from '../../img_instalacao/Instalacao04.jpeg'
import instalacao05 from '../../img_instalacao/Instalacao05.jpeg'
import instalacao06 from '../../img_instalacao/Instalacao06.jpeg'
import {
  EMPRESA_CLAREAR_ID,
  EMPRESA_HANGSIX_ID,
  formatDateBR,
  getBase64FromUrl,
  slugNomeArquivo
} from './constantes'
import { nomeArquivoHtmlDePdf, salvarHtmlProposta } from './salvarHtml'

function escapeHtmlPadrao(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function buildHtmlPropostaPadrao({
  dados,
  nomeEmpresa,
  contatoEmpresa,
  emailEmpresa,
  enderecoEmpresa
}) {
  const fmtMoeda = (n) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(n) || 0)
  const total = Number(dados.totalProposta) || 0
  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Proposta Comercial — ${escapeHtmlPadrao(dados.nomeCliente)}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; color: #0f172a; max-width: 800px; margin: 32px auto; padding: 0 20px; }
    h1 { font-size: 1.4rem; margin-bottom: 4px; }
    .sub { color: #475569; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; }
    th, td { text-align: left; padding: 10px 8px; border-bottom: 1px solid #e2e8f0; }
    th { width: 42%; color: #475569; font-weight: 600; }
    .total { font-size: 1.25rem; font-weight: 700; }
    footer { margin-top: 40px; padding-top: 16px; border-top: 3px solid #f59e0b; color: #475569; font-size: 0.9rem; }
  </style>
</head>
<body>
  <h1>${escapeHtmlPadrao(nomeEmpresa)}</h1>
  <p class="sub">Proposta comercial de energia solar fotovoltaica</p>
  <table>
    <tr><th>Cliente</th><td>${escapeHtmlPadrao(dados.nomeCliente)}</td></tr>
    ${dados.cpf ? `<tr><th>CPF</th><td>${escapeHtmlPadrao(dados.cpf)}</td></tr>` : ''}
    <tr><th>Data da proposta</th><td>${escapeHtmlPadrao(formatDateBR(dados.dataProposta))}</td></tr>
    <tr><th>Validade</th><td>${escapeHtmlPadrao(formatDateBR(dados.dataValidade))}</td></tr>
    <tr><th>Consumo mensal</th><td>${escapeHtmlPadrao(dados.kwh != null ? `${dados.kwh} kWh` : '—')}</td></tr>
    <tr><th>Potência do sistema</th><td>${escapeHtmlPadrao(`${Number(dados.projetoKwp || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWp`)}</td></tr>
    <tr><th>Módulos</th><td>${escapeHtmlPadrao(`${dados.quantidadePaineis || 0}x ${dados.moduloNome || 'Módulo'} ${dados.moduloPotencia || 0}W`)}</td></tr>
    <tr><th>Inversor</th><td>${escapeHtmlPadrao(`${dados.inversor || 0} kW`)}</td></tr>
    <tr><th>Investimento</th><td class="total">${escapeHtmlPadrao(fmtMoeda(total))}</td></tr>
  </table>
  <footer>
    <strong>${escapeHtmlPadrao(nomeEmpresa)}</strong><br />
    ${escapeHtmlPadrao([contatoEmpresa, emailEmpresa].filter(Boolean).join(' | '))}<br />
    ${escapeHtmlPadrao(enderecoEmpresa || '')}
  </footer>
</body>
</html>`
}

/**
 * @param {{ dados: object, userEmpresa: object }} args
 * @returns {Promise<{ fileName: string, htmlFileName: string, tipo: 'pdf' }>}
 */
export async function gerarPdfPadrao({ dados, userEmpresa }) {
      const pdf = new jsPDF('p', 'mm', 'a4')
      const pageWidth = pdf.internal.pageSize.getWidth()
      const pageHeight = pdf.internal.pageSize.getHeight()
      const margin = 20
      let currentY = margin

      // Paleta de cores premium e profissional
      const colors = {
        primary: [15, 23, 42],        // Slate-900 - Azul escuro premium
        secondary: [99, 102, 241],    // Indigo-500 - Roxo elegante
        accent: [16, 185, 129],       // Emerald-500 - Verde premium
        dark: [15, 23, 42],           // Slate-900 - Preto premium
        gray: [71, 85, 105],          // Slate-600 - Cinza elegante
        lightGray: [248, 250, 252],   // Slate-50 - Cinza muito claro
        gold: [245, 158, 11],         // Amber-500 - Dourado premium
        success: [34, 197, 94],       // Green-500 - Verde sucesso
        warning: [249, 115, 22],      // Orange-500 - Laranja
        info: [59, 130, 246],         // Blue-500 - Azul info
        white: [255, 255, 255],       // Branco puro
        gradient: [30, 41, 59]        // Slate-800 - Para gradientes
      }

      // Função para quebrar página se necessário
      const checkPageBreak = (requiredSpace = 10) => {
        if (currentY + requiredSpace > pageHeight - margin) {
          pdf.addPage()
          currentY = margin
          return true
        }
        return false
      }

      // Função para adicionar linha separadora
      const addSeparator = () => {
        pdf.setDrawColor(...colors.gray)
        pdf.setLineWidth(0.5)
        pdf.line(margin, currentY, pageWidth - margin, currentY)
        currentY += 5
      }

      // Verificar ID da empresa para definir cor de fundo do cabeçalho
      const empresaIdEspecial = EMPRESA_CLAREAR_ID
      const isEmpresaEspecial = userEmpresa?.id === empresaIdEspecial
      
      // Cabeçalho premium com gradiente simulado
      pdf.setFillColor(...colors.primary)
      pdf.rect(0, 0, pageWidth, 75, 'F')
      
      // Gradiente simulado com overlay - cor alterada para empresa específica
      if (isEmpresaEspecial) {
        // Cor RGB #0006B2 para empresa específica
        pdf.setFillColor(0, 6, 178)
      } else {
        pdf.setFillColor(...colors.gradient)
      }
      pdf.rect(0, 0, pageWidth, 75, 'F')
      
      // Linha decorativa dourada premium
      pdf.setFillColor(...colors.gold)
      pdf.rect(0, 70, pageWidth, 5, 'F')
      
      // Logo da empresa (esticado para os lados)
      const hangsixId = EMPRESA_HANGSIX_ID
      const isHangSix = !userEmpresa?.id || userEmpresa.id === hangsixId
      
      let logoToUse = logoHangSix
      let logoFormat = 'PNG'
      
      if (!isHangSix && userEmpresa?.logo_url) {
        const base64Logo = await getBase64FromUrl(userEmpresa.logo_url)
        if (base64Logo) {
          logoToUse = base64Logo
          if (userEmpresa.logo_url.toLowerCase().endsWith('.jpg') || userEmpresa.logo_url.toLowerCase().endsWith('.jpeg')) {
            logoFormat = 'JPEG'
          }
        }
      }

      try {
        pdf.addImage(logoToUse, logoFormat, pageWidth / 2 - 45, 2, 90, 50) 
      } catch (error) {
        console.warn('Erro ao carregar logo:', error)
        // Fallback: ícone solar simulado
        pdf.setFillColor(255, 255, 255)
        pdf.circle(pageWidth / 2, 30, 10, 'F')
        pdf.setFillColor(...colors.gold)
        pdf.circle(pageWidth / 2, 30, 8, 'F')
        pdf.setFillColor(255, 255, 255)
        pdf.circle(pageWidth / 2, 30, 3, 'F')
      }
      
      const tituloEmpresaHeader = isHangSix ? 'HANGSIX ENERGIA SOLAR' : (userEmpresa?.nome || 'HANGSIX ENERGIA SOLAR')
      const clarearId = EMPRESA_CLAREAR_ID
      const isClarear = userEmpresa?.id === clarearId

      if (!isClarear) {
        pdf.setTextColor(...colors.white)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(28)
        pdf.text(tituloEmpresaHeader.toUpperCase(), pageWidth / 2, 60, { align: 'center' })
        
        pdf.setFontSize(13)
        pdf.setTextColor(...colors.white)
        pdf.text('Soluções em Energia Solar Fotovoltaica', pageWidth / 2, 67, { align: 'center' })
      }
      
      pdf.setTextColor(0, 0, 0)
      currentY = 90

      // Título premium da proposta
      pdf.setFillColor(...colors.lightGray)
      pdf.rect(margin, currentY - 8, pageWidth - 2 * margin, 45, 'F')
      
      // Bordas decorativas premium
      pdf.setDrawColor(...colors.accent)
      pdf.setLineWidth(3)
      pdf.rect(margin + 3, currentY - 5, pageWidth - 2 * margin - 6, 39)
      
      // Sombra sutil
      pdf.setDrawColor(...colors.gray)
      pdf.setLineWidth(1)
      pdf.rect(margin + 1, currentY - 7, pageWidth - 2 * margin - 2, 43)
      
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(22)
      pdf.setTextColor(...colors.primary)
      pdf.text('PROPOSTA COMERCIAL', pageWidth / 2, currentY + 10, { align: 'center' })
      
      pdf.setFontSize(13)
      pdf.setTextColor(...colors.accent)
      pdf.text('Sistema de Energia Solar Fotovoltaica', pageWidth / 2, currentY + 22, { align: 'center' })
      
      pdf.setFontSize(11)
      pdf.setTextColor(...colors.gray)
      pdf.text('Proposta personalizada para seu projeto', pageWidth / 2, currentY + 32, { align: 'center' })
      
      currentY += 50
    
      // Informações do cliente premium
      pdf.setFillColor(...colors.accent)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F')
      
      pdf.setTextColor(...colors.white)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(15)
      pdf.text('DADOS DO CLIENTE', margin + 8, currentY + 8)
      currentY += 20

      // Caixa premium de informações do cliente
      pdf.setFillColor(...colors.white)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 40, 'F')
      
      // Bordas elegantes
      pdf.setDrawColor(...colors.accent)
      pdf.setLineWidth(2)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 40)
      
      // Sombra sutil
      pdf.setDrawColor(...colors.lightGray)
      pdf.setLineWidth(1)
      pdf.rect(margin + 1, currentY + 1, pageWidth - 2 * margin - 2, 38)

      pdf.setTextColor(...colors.dark)
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(12)
      
      const clientInfo = [
        `Nome: ${dados.nomeCliente}`,
        dados.cpf ? `CPF: ${dados.cpf}` : null,
        `Data da Proposta: ${formatDateBR(dados.dataProposta)}`,
        `Validade: ${formatDateBR(dados.dataValidade)}`
      ].filter(Boolean)

      clientInfo.forEach((info, index) => {
        pdf.text(info, margin + 12, currentY + 12 + (index * 8))
      })
      
      currentY += 50

      // Dados do sistema premium
      pdf.setFillColor(...colors.secondary)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F')
      
      pdf.setTextColor(...colors.white)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(15)
      pdf.text('DADOS DO SISTEMA', margin + 8, currentY + 8)
      currentY += 20

      // Caixa premium de informações do sistema
      pdf.setFillColor(...colors.white)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 50, 'F')
      
      // Bordas elegantes
      pdf.setDrawColor(...colors.secondary)
      pdf.setLineWidth(2)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 50)
      
      // Sombra sutil
      pdf.setDrawColor(...colors.lightGray)
      pdf.setLineWidth(1)
      pdf.rect(margin + 1, currentY + 1, pageWidth - 2 * margin - 2, 48)

      pdf.setTextColor(...colors.dark)
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(12)
      
      const systemInfo = [
        `Consumo Mensal: ${dados.kwh || 'Não informado'} kWh`,
        `Projeto Calculado: ${dados.projetoKwp || 0} kWp`,
        `Módulo Selecionado: ${dados.moduloNome || 'Não selecionado'}`,
        `Potência do Módulo: ${dados.moduloPotencia || 0}W`,
        `Quantidade de Painéis: ${dados.quantidadePaineis || 0} unidades`,
        `Inversor Recomendado: ${dados.inversor || 0} kW`
      ]

      systemInfo.forEach((info, index) => {
        pdf.text(info, margin + 12, currentY + 12 + (index * 6))
      })
      
      currentY += 60

      // Seção "Como Funciona" com diagrama visual
      const alturaSecaoCompleta = 12 + 20 + 140 + 15 // Cabeçalho + espaçamento + imagem esticada + texto explicativo
      
      // Verificar se há espaço suficiente para a seção completa
      if (currentY + alturaSecaoCompleta > pageHeight - margin - 20) {
        pdf.addPage()
        currentY = margin
      }

      pdf.setFillColor(...colors.info)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F')
      
      pdf.setTextColor(...colors.white)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(15)
      pdf.text('COMO FUNCIONA', margin + 8, currentY + 8)
      currentY += 20

      // Caixa para a imagem (aumentada)
      pdf.setFillColor(...colors.white)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 140, 'F')
      
      pdf.setDrawColor(...colors.info)
      pdf.setLineWidth(2)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 140)

      // Adicionar imagem do autoconsumo
      try {
        // Adicionar a imagem centralizada na caixa (esticada)
        const imageWidth = pageWidth - 2 * margin - 20 // Largura da imagem (deixando margem)
        const imageHeight = 120 // Altura da imagem aumentada (estica para cima e para baixo)
        const imageX = margin + 10 // Posição X centralizada
        const imageY = currentY + 10 // Posição Y com margem
        
        pdf.addImage(autoconsumoImg, 'PNG', imageX, imageY, imageWidth, imageHeight)
      } catch (error) {
        console.warn('Erro ao carregar imagem de autoconsumo:', error)
        
        // Fallback: texto explicativo se a imagem não carregar
        pdf.setTextColor(...colors.dark)
        pdf.setFontSize(12)
        pdf.setFont('helvetica', 'bold')
        pdf.text('Diagrama de Autoconsumo Solar', pageWidth / 2, currentY + 40, { align: 'center' })
        
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(10)
        pdf.text('1. Os painéis solares captam a luz do sol e geram energia elétrica', margin + 20, currentY + 60)
        pdf.text('2. O inversor converte a energia DC dos painéis para AC utilizável', margin + 20, currentY + 75)
        pdf.text('3. A energia é consumida pela casa ou injetada na rede elétrica', margin + 20, currentY + 90)
      }

      // Explicação do funcionamento abaixo da imagem
      pdf.setTextColor(...colors.dark)
      pdf.setFontSize(9)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Como funciona o sistema de autoconsumo solar:', margin + 20, currentY + 135)
      
      currentY += 150

      // Seção de Projetos Realizados (movida para cá)
      pdf.setFillColor(...colors.primary)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F')
      
      pdf.setTextColor(255, 255, 255)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(14)
      pdf.text('PROJETOS REALIZADOS', margin + 5, currentY + 7)
      currentY += 17

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(11)
      pdf.setTextColor(...colors.dark)
      
      const nomeEmpresa = userEmpresa?.nome || 'HANGSIX ENERGIA SOLAR'
      const contatoEmpresa = userEmpresa?.contato || '(91) 9 9253-3339'
      const emailEmpresa = userEmpresa?.email || 'hangsixsolar@gmail.com'
      const enderecoEmpresa = userEmpresa?.endereco || 'www.hangsix.com.br | @hangsixsolar'

      const projetosInfoMovida = [
        `A ${nomeEmpresa} possui vasta experiência no mercado de energia solar fotovoltaica,`,
        'com centenas de projetos realizados com sucesso em todo o Brasil.',
        '',
        'NOSSOS DIFERENCIAIS:',
        '• Mais de 500 sistemas instalados',
        '• Equipe técnica especializada e certificada',
        '• Materiais de primeira linha e garantia estendida',
        '• Instalação rápida e sem complicações',
        '• Suporte pós-venda completo',
        '• Parceria com as melhores marcas do mercado',
        '',
        'CASOS DE SUCESSO:',
        '• Residencial: Redução de até 85% na conta de luz',
        '• Comercial: Economia mensal de R$ 2.000 a R$ 15.000',
        '• Industrial: Payback em menos de 3 anos',
        '• Rural: Energia limpa para propriedades rurais'
      ]

      projetosInfoMovida.forEach(info => {
        if (info === '') {
          currentY += 2 // Reduzido de 3 para 2
        } else {
          if (currentY > pageHeight - 20) { // Reduzido de 30 para 20
            pdf.addPage()
            currentY = margin
          }
          
          if (info.includes('NOSSOS DIFERENCIAIS') || info.includes('CASOS DE SUCESSO')) {
            pdf.setFont('helvetica', 'bold')
            pdf.setTextColor(...colors.primary)
            pdf.setFontSize(11) // Reduzido de 12 para 11
          } else if (info.includes('•')) {
            pdf.setFont('helvetica', 'normal')
            pdf.setTextColor(...colors.dark)
            pdf.setFontSize(9) // Reduzido de 10 para 9
            pdf.text(info, margin + 5, currentY)
          } else {
            pdf.setFont('helvetica', 'normal')
            pdf.setTextColor(...colors.dark)
            pdf.setFontSize(9) // Reduzido de 10 para 9
            pdf.text(info, margin, currentY)
          }
          currentY += 3 // Reduzido de 4 para 3
        }
      })

      currentY += 15

      // Adicionar imagens de projetos realizados
      try {
        const imagens = [instalacao01, instalacao02, instalacao03, instalacao04, instalacao05, instalacao06]
        const larguraImagem = 85
        const alturaImagem = 60
        const espacamento = 10
        const alturaTitulo = 15
        const alturaTotalNecessaria = alturaTitulo + (3 * (alturaImagem + espacamento))

        // Verificar se há espaço suficiente para título + imagens na página atual
        if (currentY + alturaTotalNecessaria > pageHeight - margin - 20) {
          pdf.addPage()
          currentY = margin
        }

        // Título das imagens
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(12)
        pdf.setTextColor(...colors.primary)
        pdf.text('GALERIA DE PROJETOS REALIZADOS', margin, currentY)
        currentY += alturaTitulo

        // Organizar imagens em grupos de 2 por linha
        for (let i = 0; i < imagens.length; i += 2) {
          // Verificar se há espaço na página para uma nova linha
          if (currentY + alturaImagem > pageHeight - margin - 20) {
            pdf.addPage()
            currentY = margin
          }

          // Primeira imagem da linha
          const x1 = margin
          const y1 = currentY
          
          try {
            pdf.addImage(imagens[i], 'JPEG', x1, y1, larguraImagem, alturaImagem)
          } catch (error) {
            console.warn(`Erro ao carregar imagem ${i + 1}:`, error)
            // Placeholder para primeira imagem
            pdf.setFillColor(240, 240, 240)
            pdf.rect(x1, y1, larguraImagem, alturaImagem, 'F')
            pdf.setDrawColor(200, 200, 200)
            pdf.rect(x1, y1, larguraImagem, alturaImagem)
            pdf.setFontSize(8)
            pdf.setTextColor(150, 150, 150)
            pdf.text('Imagem não disponível', x1 + 5, y1 + alturaImagem/2)
          }

          // Segunda imagem da linha (se existir)
          if (i + 1 < imagens.length) {
            const x2 = margin + larguraImagem + espacamento
            const y2 = currentY
            
            try {
              pdf.addImage(imagens[i + 1], 'JPEG', x2, y2, larguraImagem, alturaImagem)
            } catch (error) {
              console.warn(`Erro ao carregar imagem ${i + 2}:`, error)
              // Placeholder para segunda imagem
              pdf.setFillColor(240, 240, 240)
              pdf.rect(x2, y2, larguraImagem, alturaImagem, 'F')
              pdf.setDrawColor(200, 200, 200)
              pdf.rect(x2, y2, larguraImagem, alturaImagem)
              pdf.setFontSize(8)
              pdf.setTextColor(150, 150, 150)
              pdf.text('Imagem não disponível', x2 + 5, y2 + alturaImagem/2)
            }
          }

          // Mover para a próxima linha
          currentY += alturaImagem + espacamento
        }

        currentY += 10

      } catch (error) {
        console.warn('Erro ao processar imagens:', error)
        currentY += 10
      }

      // Seção sobre Energia Solar premium
      pdf.setFillColor(...colors.secondary)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F')
      
      pdf.setTextColor(...colors.white)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(15)
      pdf.text('ENERGIA SOLAR: UMA ESCOLHA INTELIGENTE', margin + 8, currentY + 8)
      currentY += 20

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(11)
      pdf.setTextColor(...colors.dark)
      
      const infoEnergiaSolar = [
        'A energia solar fotovoltaica é uma tecnologia que converte a luz do sol diretamente em',
        'eletricidade através de painéis solares. É uma fonte de energia limpa, renovável e',
        'sustentável que oferece inúmeros benefícios para residências e empresas.',
        '',
        'BENEFICIOS DA ENERGIA SOLAR:',
        '• Redução de até 85% na conta de energia elétrica',
        '• Valorização do imóvel em até 6%',
        '• Proteção contra aumentos tarifários',
        '• Energia limpa e sustentável',
        '• Retorno do investimento em 3 a 5 anos',
        '• Baixa manutenção e alta durabilidade',
        '',
        'COMO FUNCIONA:',
        '1. Os painéis solares captam a luz do sol',
        '2. O inversor converte a energia para uso doméstico',
        '3. A energia é distribuída pela casa normalmente',
        '4. O excedente é injetado na rede elétrica',
        '5. Você ganha créditos na conta de energia'
      ]

      infoEnergiaSolar.forEach(info => {
        if (info === '') {
          currentY += 3
        } else {
          if (currentY > pageHeight - 30) {
            pdf.addPage()
            currentY = margin
          }
          
          if (info.includes('BENEFICIOS') || info.includes('COMO FUNCIONA')) {
            pdf.setFont('helvetica', 'bold')
            pdf.setTextColor(...colors.primary)
            pdf.setFontSize(12)
          } else if (info.includes('•')) {
            pdf.setFont('helvetica', 'normal')
            pdf.setTextColor(...colors.dark)
            pdf.setFontSize(10)
            pdf.text(info, margin + 5, currentY)
          } else {
            pdf.setFont('helvetica', 'normal')
            pdf.setTextColor(...colors.dark)
            pdf.setFontSize(10)
            pdf.text(info, margin, currentY)
          }
          currentY += 4
        }
      })

      currentY += 10

      // Especificações técnicas elegantes
      pdf.setFillColor(...colors.accent)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F')
      
      pdf.setTextColor(255, 255, 255)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(14)
      pdf.text('ESPECIFICACOES TECNICAS', margin + 5, currentY + 7)
      currentY += 17

      // Verificar se há espaço suficiente na página
      if (currentY > pageHeight - 80) {
        pdf.addPage()
        currentY = margin
      }

      // Debug: Adicionar uma linha de teste primeiro
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(11)
      pdf.setTextColor(0, 0, 0)
      pdf.text('=== DADOS DO PROJETO ===', margin, currentY)
      currentY += 8
      
      // Especificações técnicas com verificações
      const specs = [
        `Consumo Mensal: ${dados.kwh || 'Não informado'} kWh`,
        `Projeto Calculado: ${dados.projetoKwp || 0} kWp`,
        `Módulo Selecionado: ${dados.moduloNome || 'Não selecionado'}`,
        `Potência do Módulo: ${dados.moduloPotencia || 0}W`,
        `Quantidade de Painéis: ${dados.quantidadePaineis || 0} unidades`,
        `Inversor Recomendado: ${dados.inversor || 0} kW`,
      ]

      // Adicionar cada especificação
      specs.forEach((spec, index) => {
        if (currentY > pageHeight - 20) {
          pdf.addPage()
          currentY = margin
        }
        
        // Destacar algumas informações importantes
        if (spec.includes('Projeto Calculado') || spec.includes('Quantidade de Painéis')) {
          pdf.setFont('helvetica', 'bold')
          pdf.setFontSize(12)
        } else {
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(11)
        }
        
        pdf.setTextColor(0, 0, 0)
        pdf.text(spec, margin, currentY)
        currentY += 6
      })

      currentY += 10

      // Informações adicionais elegantes
      const sections = [
        {
          title: 'CONDICOES DA PROPOSTA',
          color: colors.accent,
          items: [
            '• Esta proposta tem validade de 3 dias a partir da data de emissão.',
            '• O valor inclui instalação e materiais conforme especificado.',
            '• Prazo de instalação: 15 a 30 dias após aprovação.',
            '• Garantia: 5 anos para equipamentos e 2 anos para instalação.',
            '• Financiamento disponível através de parceiros bancários.'
          ]
        },
        {
          title: 'GARANTIAS E SUPORTE',
          color: colors.secondary,
          items: [
            '• Garantia de performance dos painéis: 25 anos',
            '• Garantia do inversor: 5 anos',
            '• Suporte técnico especializado',
            '• Monitoramento remoto do sistema'
          ]
        }
      ]

      sections.forEach(section => {
        if (currentY > pageHeight - 50) {
          pdf.addPage()
          currentY = margin
        }

        // Cabeçalho da seção
        pdf.setFillColor(...section.color)
        pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F')
        
        pdf.setTextColor(255, 255, 255)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(13)
        pdf.text(section.title, margin + 5, currentY + 7)
        currentY += 17

        // Itens da seção
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(11)
        pdf.setTextColor(...colors.dark)
        
        section.items.forEach(item => {
          if (currentY > pageHeight - 20) {
            pdf.addPage()
            currentY = margin
          }
          pdf.text(item, margin + 8, currentY)
          currentY += 5
        })
        
        currentY += 8
      })

      // Seção ECONOMIA ESTIMADA com gráfico de pizza
      if (currentY > pageHeight - 100) {
        pdf.addPage()
        currentY = margin
      }

      // Cabeçalho da seção ECONOMIA ESTIMADA
      pdf.setFillColor(...colors.success)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 10, 'F')
      
      pdf.setTextColor(255, 255, 255)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(13)
      pdf.text('ECONOMIA ESTIMADA', margin + 5, currentY + 7)
      currentY += 20

      // Dados para o gráfico
      const economiaAnual = Math.round(dados.totalComPorcentagem * 0.15)
      const reducaoPercentual = 85
      const paybackAnos = 4
      const valorizacaoPercentual = 6

      // Dados do gráfico de pizza (usando valores proporcionais para visualização)
      const pieData = [
        { label: 'Redução na Conta', value: reducaoPercentual, color: [34, 197, 94], display: `${reducaoPercentual}%` },
        { label: 'Economia Anual', value: economiaAnual, color: [16, 185, 129], display: `R$ ${economiaAnual.toLocaleString('pt-BR')}` },
        { label: 'Payback', value: paybackAnos * 20, color: [59, 130, 246], display: `${paybackAnos} anos` },
        { label: 'Valorização', value: valorizacaoPercentual * 10, color: [245, 158, 11], display: `${valorizacaoPercentual}%` }
      ]

      // Calcular total para proporções
      const total = pieData.reduce((sum, item) => sum + item.value, 0)

      // Dimensões do gráfico de pizza
      const pieRadius = 35
      const pieCenterX = margin + 50
      const pieCenterY = currentY + 45
      const legendStartX = pieCenterX + pieRadius + 25
      const legendStartY = currentY + 10

      // Função para converter graus em radianos
      const toRadians = (degrees) => degrees * (Math.PI / 180)

      // Função para desenhar setor (fatia) do gráfico de pizza
      const drawPieSlice = (centerX, centerY, radius, startAngle, endAngle, color) => {
        const startRad = toRadians(startAngle)
        const endRad = toRadians(endAngle)
        const midAngle = (startAngle + endAngle) / 2
        const midRad = toRadians(midAngle)
        
        // Preencher a fatia usando círculos concêntricos pequenos
        pdf.setFillColor(...color)
        pdf.setDrawColor(...color)
        pdf.setLineWidth(0.1)
        
        // Preencher do centro para fora usando círculos pequenos (otimizado)
        const fillSteps = 12
        const stepSize = radius / fillSteps
        for (let r = stepSize; r < radius; r += stepSize) {
          const angleSteps = Math.max(4, Math.ceil((endAngle - startAngle) / 4))
          for (let a = 0; a <= angleSteps; a++) {
            const angle = startAngle + (endAngle - startAngle) * (a / angleSteps)
            const rad = toRadians(angle)
            const x = centerX + r * Math.cos(rad)
            const y = centerY + r * Math.sin(rad)
            pdf.circle(x, y, 1.2, 'F')
          }
        }
        
        // Desenhar bordas da fatia
        pdf.setDrawColor(...colors.dark)
        pdf.setLineWidth(1.2)
        
        // Linha inicial (do centro até a borda)
        pdf.line(centerX, centerY, centerX + radius * Math.cos(startRad), centerY + radius * Math.sin(startRad))
        
        // Linha final (do centro até a borda)
        pdf.line(centerX, centerY, centerX + radius * Math.cos(endRad), centerY + radius * Math.sin(endRad))
        
        // Desenhar arco externo (borda curva)
        const arcSteps = Math.ceil((endAngle - startAngle) / 0.5)
        for (let i = 1; i <= arcSteps; i++) {
          const angle = startAngle + (endAngle - startAngle) * (i / arcSteps)
          const rad = toRadians(angle)
          const x = centerX + radius * Math.cos(rad)
          const y = centerY + radius * Math.sin(rad)
          
          const prevAngle = startAngle + (endAngle - startAngle) * ((i - 1) / arcSteps)
          const prevRad = toRadians(prevAngle)
          const prevX = centerX + radius * Math.cos(prevRad)
          const prevY = centerY + radius * Math.sin(prevRad)
          pdf.line(prevX, prevY, x, y)
        }
      }

      // Desenhar cada fatia do gráfico de pizza
      let startAngle = -90 // Começar no topo
      pieData.forEach((item) => {
        const sliceAngle = (item.value / total) * 360
        const endAngle = startAngle + sliceAngle
        
        // Desenhar a fatia
        drawPieSlice(pieCenterX, pieCenterY, pieRadius, startAngle, endAngle, item.color)
        
        startAngle = endAngle
      })

      // Desenhar borda externa do gráfico
      pdf.setDrawColor(...colors.dark)
      pdf.setLineWidth(1.5)
      pdf.circle(pieCenterX, pieCenterY, pieRadius, 'D')

      // Legenda do gráfico
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(9)
      pdf.setTextColor(...colors.dark)
      
      let legendY = legendStartY
      pieData.forEach((item) => {
        // Quadrado colorido da legenda
        pdf.setFillColor(...item.color)
        pdf.setDrawColor(...colors.dark)
        pdf.setLineWidth(0.5)
        pdf.rect(legendStartX, legendY - 3, 5, 5, 'FD')
        
        // Texto da legenda
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(9)
        pdf.setTextColor(...colors.dark)
        pdf.text(item.label + ':', legendStartX + 8, legendY)
        
        pdf.setFont('helvetica', 'normal')
        pdf.text(item.display, legendStartX + 8 + pdf.getTextWidth(item.label + ': ') + 2, legendY)
        
        legendY += 8
      })

      currentY = pieCenterY + pieRadius + 15

      // Legenda adicional
      pdf.setFont('helvetica', 'italic')
      pdf.setFontSize(8)
      pdf.setTextColor(...colors.gray)
      pdf.text('* Valores baseados em estimativas médias do mercado', margin + 5, currentY)
      currentY += 10

      currentY += 10

      // Investimento premium - apenas valor total (movido para depois das condições)
      pdf.setFillColor(...colors.primary)
      pdf.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F')
      
      pdf.setTextColor(...colors.white)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(15)
      pdf.text('INVESTIMENTO DO PROJETO', margin + 8, currentY + 8)
      currentY += 20

      // Valor total premium com gradiente
      pdf.setFillColor(...colors.success)
      pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 25, 'F')
      
      // Overlay para gradiente
      pdf.setFillColor(...colors.accent)
      pdf.rect(margin + 1, currentY - 2, pageWidth - 2 * margin - 2, 23, 'F')
      
      pdf.setDrawColor(...colors.dark)
      pdf.setLineWidth(2)
      pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 25)
      
      // Sombra sutil
      pdf.setDrawColor(...colors.gray)
      pdf.setLineWidth(1)
      pdf.rect(margin + 1, currentY - 2, pageWidth - 2 * margin - 2, 23)
      
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(18)
      pdf.setTextColor(...colors.white)
      pdf.text('VALOR TOTAL DO PROJETO', margin + 12, currentY + 10)
      const totalComMajoracao = Number(dados.totalProposta) || 0
      pdf.text(`R$ ${totalComMajoracao.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, pageWidth - margin - 40, currentY + 10)
      
      // Texto explicativo
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(11)
      pdf.setTextColor(...colors.white)
      pdf.text('Valor inclui equipamentos, instalação e materiais', margin + 12, currentY + 18)
      
      currentY += 30

      // Rodapé premium - posicionado mais para baixo
      if (currentY > pageHeight - 80) {
        pdf.addPage()
        currentY = margin
      }

      // Adicionar espaço extra para posicionar o rodapé mais para baixo
      currentY = pageHeight - 60 // Posiciona o rodapé a 60mm do final da página

      // Linha decorativa superior premium
      pdf.setFillColor(...colors.gold)
      pdf.rect(0, currentY - 8, pageWidth, 5, 'F')
      
      // Fundo do rodapé com gradiente
      pdf.setFillColor(...colors.primary)
      pdf.rect(0, currentY - 3, pageWidth, 45, 'F')
      
      // Overlay para gradiente
      pdf.setFillColor(...colors.gradient)
      pdf.rect(0, currentY - 3, pageWidth, 45, 'F')

      // Rodapé dinâmico baseado na empresa do usuário
      pdf.setTextColor(...colors.white)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(14)
      pdf.text(nomeEmpresa.toUpperCase(), pageWidth / 2, currentY + 10, { align: 'center' })
      
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.text(`${contatoEmpresa} | ${emailEmpresa}`, pageWidth / 2, currentY + 20, { align: 'center' })
      pdf.text(enderecoEmpresa, pageWidth / 2, currentY + 30, { align: 'center' })
      
      // Linha decorativa inferior premium
      pdf.setFillColor(...colors.gold)
      pdf.rect(0, currentY + 35, pageWidth, 3, 'F')

      // Salvar o PDF
      const fileName = `${dados.fileNameBase || `Proposta_Solar_${slugNomeArquivo(dados.nomeCliente)}_${new Date().toISOString().split('T')[0]}`}.pdf`
      pdf.save(fileName)
      const htmlFileName = nomeArquivoHtmlDePdf(fileName)
      await new Promise((r) => setTimeout(r, 400))
      salvarHtmlProposta(
        buildHtmlPropostaPadrao({
          dados,
          nomeEmpresa,
          contatoEmpresa,
          emailEmpresa,
          enderecoEmpresa
        }),
        htmlFileName
      )
      return { fileName, htmlFileName, tipo: 'pdf' }

}
